import { createContext, useContext, ReactNode, useMemo, useCallback, useState } from 'react';
import { useFormik, FormikProvider, FormikHelpers } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import { ClientProfileContentItem, removeNote } from 'store/clientProfile/clientProfileSlice';
import { Status as ClientStatus, typesOfSupportData } from 'store/clients/clientsSlice';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import dayjs from 'dayjs';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { getCurrentNote, removeCurrentNote } from 'modules/networkTools/localStorageTokens';
import {
  deleteNoteInput,
  getClientNote,
  putUpdateNote
} from 'store/clientProfile/clientProfileActions';
import { getMpi } from 'store/clients/clientsActions';
import { getTodos as getMyTodos } from 'store/myTodos/myTodosActions';
import { getTodos as getRoadmapTodos } from 'store/roadmaps/roadmapsActions';
import { notifyUserSuccess } from 'utils/notifications';

import { validationSchema, NoteFormValues, sortTypesOfSupportValues } from '../utils/validation';
import { createDefaultValues, remapValues, removeEmptyFieldsFromObject } from '../utils/helpers';
import { AutosaveProvider } from './autosaveContext';
import { isMpiNoteType } from '../presets/MpiNoteActions';

export type NoteFormContextValue = {
  state: {
    disabled: boolean;
    isManagerReview: boolean;
    isAutosaving: boolean;
    isAutosaveQueued: boolean;
    isAutosaveBusy: boolean;
    isAiScribeEnabled: boolean;
  };
  actions: {
    deleteNote: () => Promise<void>;
    resetNote: () => void;
    clearLastSaved: () => void;
  };
  meta: {
    clientId: string;
    clientName: string;
    prependedClientId: string;
    note?: Partial<ClientProfileContentItem>;
    status: ClientStatus['id'];
  };
  // Kept while existing field components migrate to state/actions/meta.
  clientId: string;
  clientName: string;
  prependedClientId: string;
  note?: Partial<ClientProfileContentItem>;
  disabled: boolean;
  isManagerReview: boolean;
  status: ClientStatus['id'];
  onOpenAiScribeDrawer?: () => void;
};

const NoteFormContext = createContext<NoteFormContextValue | null>(null);

export const useNoteFormContext = () => {
  const context = useContext(NoteFormContext);
  if (!context) {
    throw new Error('useNoteFormContext must be used within a NoteFormProvider');
  }
  return context;
};

type NoteFormProviderProps = {
  status: ClientStatus | ClientStatus['id'];
  children: ReactNode;
  clientId: string;
  clientName: string;
  prependedClientId: string;
  note?: Partial<ClientProfileContentItem>;
  disabled?: boolean;
  onSubmitSuccess?: () => void;
  onDeleteSuccess?: () => void;
  onOpenAiScribeDrawer?: () => void;
  useCurrentNoteFallback?: boolean;
};

type NoteFormProviderBaseProps = NoteFormProviderProps & {
  autosave?: boolean;
  isPopup?: boolean;
  onOpenAiScribeDrawer?: () => void;
};

export const NoteFormProviderBase = ({
  status,
  children,
  clientId,
  clientName,
  prependedClientId,
  note,
  disabled = false,
  onSubmitSuccess,
  onDeleteSuccess,
  onOpenAiScribeDrawer,
  useCurrentNoteFallback = false,
  autosave = false,
  isPopup = false
}: NoteFormProviderBaseProps) => {
  const memoizedUuid = useMemo(() => uuidv4(), []);
  const dispatch = useAppDispatch();
  const sitesEnabled = useAppSelector((state) => state.clients.client.data?.enableProgramSites);
  const isBillingEnabled = useAppSelector((state) => state.clients.client.data?.enableBilling);
  const aiScribeEnabled = useAppSelector((state) => state.clients.client.data?.aiScribeEnabled);
  const { enableDateTimeUpdates } = useFlags();
  const [isAutosaving, setIsAutosaving] = useState(false);
  const [isAutosaveQueued, setIsAutosaveQueued] = useState(false);
  const [lastSaved, setLastSaved] = useState<string | null>(null);
  const statusId = typeof status === 'string' ? status : status.id;

  const clearLastSaved = useCallback(() => {
    setLastSaved(null);
  }, []);

  const getStoredCurrentNoteIdForClient = useCallback(() => {
    const currentNote = getCurrentNote();
    const currentNoteId = currentNote?.currentNoteID?.toString();
    const currentNoteClientId = currentNote?.clientId?.toString();

    if (!currentNoteId || currentNoteClientId !== clientId.toString()) {
      return undefined;
    }

    return currentNoteId;
  }, [clientId]);

  const getFormNoteId = useCallback(
    (values?: Partial<NoteFormValues>) => {
      const noteId = note?.id?.toString() ?? values?.id?.toString();

      if (noteId) return noteId;

      return useCurrentNoteFallback ? getStoredCurrentNoteIdForClient() : undefined;
    },
    [note?.id, useCurrentNoteFallback, getStoredCurrentNoteIdForClient]
  );

  const clearStoredCurrentNoteForForm = useCallback(
    (formNoteId?: string | number | null) => {
      const storedCurrentNoteId = getStoredCurrentNoteIdForClient();

      if (!storedCurrentNoteId) return;

      if (!formNoteId) {
        if (useCurrentNoteFallback) removeCurrentNote();
        return;
      }

      if (storedCurrentNoteId === formNoteId.toString()) {
        removeCurrentNote();
      }
    },
    [getStoredCurrentNoteIdForClient, useCurrentNoteFallback]
  );

  const handleSubmit = useCallback(
    async (values: NoteFormValues, helpers: FormikHelpers<NoteFormValues>) => {
      const noteId = getFormNoteId(values);
      if (!noteId) return;

      const dataUpdate = remapValues({
        values,
        clientId,
        id: noteId,
        draft: false
      });
      const res = await dispatch(putUpdateNote(removeEmptyFieldsFromObject(dataUpdate) as any));

      if (res.meta.requestStatus === 'fulfilled') {
        const channel = new BroadcastChannel('note-update');
        channel.postMessage({ message: 'EDIT_NOTE', data: dataUpdate });

        if (values.noteType === 'SAFETY_NOTE' || note?.noteType === 'SAFETY_NOTE') {
          channel.postMessage({ message: 'SAFETY_NOTE' });
        }

        if (values.nextContact != null && values.nextContact !== '') {
          notifyUserSuccess('Next contact to-do created');
          if (isPopup) {
            channel.postMessage({ message: 'NEXT_CONTACT_TODO' });
          } else {
            dispatch(getMyTodos());
            dispatch(getRoadmapTodos({ clientId }));
          }
        }

        if (isMpiNoteType(note?.noteType) || isMpiNoteType(values.noteType)) {
          if (isPopup) {
            channel.postMessage({ message: 'MPI_NOTE_EDITED' });
          } else {
            dispatch(getMpi({ clientId }));
          }
        }

        channel.close();
        clearStoredCurrentNoteForForm(noteId);
        clearLastSaved();
        notifyUserSuccess('Note saved.');

        helpers.resetForm({
          values: createDefaultValues(sitesEnabled, isBillingEnabled)
        });
        dispatch(removeNote());

        if (isPopup) {
          await dispatch(
            getClientNote({
              clientId,
              noteId
            })
          );
        }

        onSubmitSuccess?.();
      }
    },
    [
      note,
      clientId,
      dispatch,
      isPopup,
      getFormNoteId,
      clearStoredCurrentNoteForForm,
      clearLastSaved,
      sitesEnabled,
      isBillingEnabled,
      onSubmitSuccess
    ]
  );

  const formik = useFormik<NoteFormValues>({
    initialValues: {
      note: note?.note || null,
      draft: note?.draft || true,
      noteType: note?.noteType || null,
      clientReached: note?.clientReached || null,
      dateOfEncounter:
        enableDateTimeUpdates && note?.dateAndTimeOfEncounter
          ? dayjs(note?.dateAndTimeOfEncounter).format('MM/DD/YYYY')
          : note?.dateOfEncounter || null,
      startTimeOfEncounter:
        enableDateTimeUpdates && note?.dateAndTimeOfEncounter
          ? dayjs(note?.dateAndTimeOfEncounter).format('hh:mm A')
          : enableDateTimeUpdates && note?.startTimeOfEncounter
          ? dayjs(
              `${dayjs().format('YYYY-MM-DD')}T${dayjs(
                note?.startTimeOfEncounter,
                'hh:mm A'
              ).format('HH:mm:ss')}Z`
            ).format('hh:mm A')
          : note?.startTimeOfEncounter || null,
      nextContact: note?.nextContact || null,
      typeOfSupport: note?.typeOfSupport
        ? sortTypesOfSupportValues(note.typeOfSupport, typesOfSupportData)
        : [],
      id: note?.id || null,
      timeSpentMin:
        note?.timeSpentMin?.toString() == null && note?.timeSpentMin !== 0
          ? null
          : note?.timeSpentMin?.toString(),
      siteId: note?.siteId || null,
      uuid: note?.uuid ?? memoizedUuid,
      selectedZCodes:
        note?.selectedZCodes?.map((z) => ({
          id: z.id,
          name: `${z.code} - ${z.description}`,
          explanations: z.explanations,
          selectedExplanations: z.selectedExplanations?.map((e: any) => e.id.toString()) || []
        })) || [],
      whoDidYouReach: note?.whoDidYouReach || [],
      whoWereYouTryingToReach: note?.whoWereYouTryingToReach || '',
      howDidYouTryThem: note?.howDidYouTryThem || '',
      howDidYouReachThem: note?.howDidYouReachThem || null,
      whatTypeOfPlaceWereYouIn: note?.whatTypeOfPlaceWereYouIn || '',
      billingProviderId: note?.billingProviderId?.toString() || null,
      otherProviderName: note?.otherProviderName || '',
      documentationTime: note?.documentationTime || null,
      sitesEnabled: sitesEnabled,
      isBillingEnabled: isBillingEnabled,
      chwId: note?.chwId || null,
      managerId: note?.managerId || null
    },
    validateOnMount: true,
    validationSchema,
    onSubmit: handleSubmit
  });

  const isManagerReview = formik.values.noteType === 'MANAGER_REVIEW';

  const handleReset = useCallback(() => {
    const formNoteId = getFormNoteId(formik.values);
    formik.resetForm({
      values: createDefaultValues(sitesEnabled, isBillingEnabled)
    });
    clearStoredCurrentNoteForForm(formNoteId);
    dispatch(removeNote());
    clearLastSaved();
  }, [
    formik,
    getFormNoteId,
    sitesEnabled,
    isBillingEnabled,
    clearStoredCurrentNoteForForm,
    dispatch,
    clearLastSaved
  ]);

  const handleDelete = useCallback(async () => {
    const noteId = getFormNoteId(formik.values);
    formik.resetForm({
      values: createDefaultValues(sitesEnabled, isBillingEnabled)
    });
    clearLastSaved();
    dispatch(removeNote());

    if (noteId) {
      await dispatch(
        deleteNoteInput({
          clientId,
          noteId
        })
      );

      clearStoredCurrentNoteForForm(noteId);
      const channel = new BroadcastChannel('note-update');
      channel.postMessage({ message: 'DELETE_NOTE', noteId });
      if (note?.noteType === 'SAFETY_NOTE') {
        channel.postMessage({ message: 'SAFETY_NOTE' });
      }
      channel.close();

      if (isPopup) {
        window.close();
      }
    }
    onDeleteSuccess?.();
  }, [
    formik,
    sitesEnabled,
    isBillingEnabled,
    getFormNoteId,
    clearStoredCurrentNoteForForm,
    clearLastSaved,
    dispatch,
    note,
    clientId,
    isPopup,
    onDeleteSuccess
  ]);

  const actions = useMemo(
    () => ({
      deleteNote: handleDelete,
      resetNote: handleReset,
      clearLastSaved
    }),
    [handleDelete, handleReset, clearLastSaved]
  );

  const state = useMemo(
    () => ({
      disabled,
      isManagerReview,
      isAutosaving,
      isAutosaveQueued,
      isAutosaveBusy: isAutosaving || isAutosaveQueued,
      isAiScribeEnabled: aiScribeEnabled ?? false
    }),
    [disabled, isManagerReview, isAutosaving, isAutosaveQueued, aiScribeEnabled]
  );

  const meta = useMemo(
    () => ({
      clientId,
      clientName,
      prependedClientId,
      note,
      status: statusId
    }),
    [clientId, clientName, prependedClientId, note, statusId]
  );

  const contextValue = useMemo(
    () => ({
      state,
      actions,
      meta,
      clientId,
      clientName,
      prependedClientId,
      note,
      disabled,
      isManagerReview,
      status: statusId,
      onOpenAiScribeDrawer,
      aiScribeEnabled
    }),
    [
      state,
      actions,
      meta,
      clientId,
      clientName,
      prependedClientId,
      note,
      disabled,
      isManagerReview,
      statusId,
      onOpenAiScribeDrawer,
      aiScribeEnabled
    ]
  );

  const content = autosave ? (
    <AutosaveProvider
      state={{ isAutosaving, isAutosaveQueued, lastSaved }}
      actions={{ setIsAutosaving, setIsAutosaveQueued, setLastSaved, clearLastSaved }}
    >
      {children}
    </AutosaveProvider>
  ) : (
    children
  );

  return (
    <NoteFormContext.Provider value={contextValue}>
      <FormikProvider value={formik}>{content}</FormikProvider>
    </NoteFormContext.Provider>
  );
};

export const DraftNoteFormProvider = (props: NoteFormProviderProps) => {
  return <NoteFormProviderBase {...props} autosave />;
};

export const PopupDraftNoteFormProvider = (props: NoteFormProviderProps) => {
  return <NoteFormProviderBase {...props} autosave isPopup />;
};

export const FinalizedNoteFormProvider = (props: NoteFormProviderProps) => {
  return <NoteFormProviderBase {...props} isPopup />;
};

export const NoteFormProvider = NoteFormProviderBase;
